import type { AnyFieldApi, AnyFormApi } from '@tanstack/react-form';

import type { ObfuscatedLoanIdentifier } from '~/common/hooks/useUrl';

import type { IncomeBorrower, IncomeRow } from '../types';
import { nonEmploymentIncomeTypeOptions } from './income1003Constants';
import { calculateElapsedYearsMonths } from './income1003Utils';

type OnChangeHandlerContext = {
  obfuscatedIds: ObfuscatedLoanIdentifier;
  allBorrowerOptions: IncomeBorrower[];
  rowToEdit: IncomeRow;
};

export function newEmploymentDetailsObject(
  obfuscatedIds: ObfuscatedLoanIdentifier,
  rowToEdit: IncomeRow,
) {
  return {
    incomeIdentifier: {
      obfuscatedLenderDatabaseId: obfuscatedIds.obfuscatedLenderDatabaseId,
      customerRecordId: rowToEdit.customerRecordId,
      employerId: rowToEdit.employerId,
      sequenceNumber: 0,
    },
    incomeType: '',
    monthlyNetIncome: null,
    period: 0,
    taxable: '',
    hoursPerWeek: 0,
    incomeVerifiedDate: '',
    incomeVerifiedFlag: '',
  };
}

export function newCpaInformationObject() {
  return {
    customerCpaDetailId: 0,
    lenderDatabaseId: 0,
    customerRecordId: 0,
    employerId: 0,
    cpaFirstName: '',
    cpaMiddleName: '',
    cpaLastName: '',
    cpaCompanyName: '',
  };
}

function handleCurrentEmploymentChange(value: boolean, formApi: AnyFormApi) {
  if (!value) return;
  formApi.setFieldValue('endDate', null);
  const newElapsedTime = calculateElapsedYearsMonths(
    formApi.state.values.startDate,
  );
  formApi.setFieldValue('yearsOnJob', newElapsedTime.years);
  formApi.setFieldValue('monthsOnJob', newElapsedTime.months);
}

function handleStartEndDateChange(
  field: string,
  value: string,
  formApi: AnyFormApi,
) {
  if (field === 'endDate') {
    const newCurrentEmployment = !value;
    if (formApi.state.values.currentEmployment !== newCurrentEmployment) {
      formApi.setFieldValue('currentEmployment', newCurrentEmployment);
    }
  }

  const values = formApi.state.values;
  const startDate = field === 'startDate' ? value : values.startDate;
  const endDate = field === 'endDate' ? value : values.endDate;
  const newElapsedTime = calculateElapsedYearsMonths(startDate, endDate);
  formApi.setFieldValue('yearsOnJob', newElapsedTime.years);
  formApi.setFieldValue('monthsOnJob', newElapsedTime.months);
}

function handleBorrowerNameChange(
  customerId: number,
  formApi: AnyFormApi,
  allBorrowerOptions: IncomeBorrower[],
) {
  const borrower =
    allBorrowerOptions.find((obj) => obj.customerRecordId === +customerId) ??
    {};
  formApi.setFieldValue('borrowerName', {
    firstName: borrower.firstName,
    lastName: borrower.lastName,
    middleName: borrower.middleName ?? '',
    suffixName: borrower.suffixName ?? '',
  });
  formApi.setFieldValue('borrowerSSN', borrower.socialSecurityNumber ?? null);
}

function handleSwitchToNonEmployment(
  formApi: AnyFormApi,
  obfuscatedIds: ObfuscatedLoanIdentifier,
  rowToEdit: IncomeRow,
) {
  const formData = formApi.state.values;
  formApi.reset({
    borrowerName: formData.borrowerName,
    customerRecordId: formData.customerRecordId,
    currentEmployment: formData.currentEmployment,
    employerId: formData.employerId,
    lenderDatabaseId: formData.lenderDatabaseId,
    incomeSource: 'Non-Employment',
    employmentType: [],
    borrowerSSN: formData.borrowerSSN,
    employerPhone: '',
    employmentDetails: [
      { ...newEmploymentDetailsObject(obfuscatedIds, rowToEdit) },
    ],
    employerName: formData.employerName,
    empByPartyToTranFlag: 0,
    percentBusinessOwned: '',
    country: '',
    employerAddress: {
      addressLine1: '',
      city: '',
      state: '',
      zipCode: '',
    },
    title: '',
    typeOfBusiness: '',
    startDate: '',
    endDate: '',
    yearsOnJob: 0,
    monthsOnJob: 0,
    verifiedDate: '',
    cpaInformation: [],
    lessThan25PercentOwnershipFlag: false,
  });
}

function handleChange(
  fieldApi: AnyFieldApi,
  formApi: AnyFormApi,
  context: OnChangeHandlerContext,
) {
  const fieldName = fieldApi.name;
  const fieldValue = fieldApi.state.value;

  switch (fieldName) {
    case 'startDate':
    case 'endDate':
      handleStartEndDateChange(fieldName, fieldValue, formApi);
      break;
    case 'customerRecordId':
      handleBorrowerNameChange(fieldValue, formApi, context.allBorrowerOptions);
      break;
    case 'incomeSource':
      if (fieldValue === 'Non-Employment') {
        handleSwitchToNonEmployment(
          formApi,
          context.obfuscatedIds,
          context.rowToEdit,
        );
      }
      break;
    case 'currentEmployment':
      handleCurrentEmploymentChange(fieldValue, formApi);
      break;
  }
}

export function createOnChangeHandler(context: OnChangeHandlerContext) {
  return ({
    fieldApi,
    formApi,
  }: {
    fieldApi: AnyFieldApi;
    formApi: AnyFormApi;
  }) => {
    if (
      fieldApi.name === 'incomeSource' &&
      fieldApi.state.value === 'Non-Employment' &&
      formApi.state.values.employmentDetails.length === 0
    ) {
      formApi.setFieldValue('employmentDetails', [
        newEmploymentDetailsObject(context.obfuscatedIds, context.rowToEdit),
      ]);
    }

    if (fieldApi.name === 'percentBusinessOwned') {
      const pct = Number(fieldApi.state.value);
      formApi.setFieldValue(
        'lessThan25PercentOwnershipFlag',
        pct !== 0 && fieldApi.state.value !== '' && pct < 25,
      );
    }

    if (
      fieldApi.name.endsWith('.incomeType') &&
      formApi.state.values.incomeSource === 'Non-Employment'
    ) {
      const label = nonEmploymentIncomeTypeOptions.find(
        (o) => o.typeCode === fieldApi.state.value,
      )?.type;
      if (label) {
        formApi.setFieldValue('employerName', label);
      }
    }

    handleChange(fieldApi, formApi, context);
  };
}
